<template>
  <Modal
    :show="isVisible"
    :title="modalTitle"
    :close-label="t('Fechar')"
    size="large"
    @close="closeModal"
    @dismiss="closeModal"
  >
    <template v-if="isVisible && currentTicket">
      <div class="ticket-detail">
        <!-- Ticket Info Card -->
        <box class="ticket-info-card">
          <template #header>
            <div class="ticket-card-header">
              <div class="ticket-title-row">
                <Tag :severity="getStatusSeverity(currentTicket.stage_name)" :value="currentTicket.stage_name || t('Novo')" />
                <span class="ticket-id-badge">#{{ currentTicket.id }}</span>
              </div>
              <Button
                v-if="currentTicket.page_uri"
                :label="t('Ver página')"
                icon="pi pi-external-link"
                severity="secondary"
                size="small"
                outlined
                as="a"
                :href="currentTicket.page_uri"
                target="_blank"
                data-testid="support-detail-view-page-button"
              />
            </div>
          </template>
          <div class="ticket-info-body">
            <span class="ticket-subject-title">{{ currentTicket.name }}</span>
            <div class="ticket-subtitle">
              <i class="pi pi-calendar"></i>
              <span>{{ t('Criado em') }}: {{ formatTicketDateTime(currentTicket.created_at) }}</span>
            </div>
            <Panel :header="t('Descrição Original')" toggleable class="description-panel">
              <div class="description-content" v-html="sanitizeHtmlContent(currentTicket.user_description || currentTicket.description)"></div>
            </Panel>
          </div>
        </box>

        <Divider />

        <!-- Conversation Section -->
        <div class="conversation-section">
          <div class="conversation-header">
            <h4>
              <i class="pi pi-comments"></i>
              {{ t('Conversação') }}
              <Badge :value="ticketMessages.length" severity="secondary" />
            </h4>
            <Button
              icon="pi pi-refresh"
              severity="secondary"
              size="small"
              rounded
              outlined
              :loading="isLoadingMessages"
              v-tooltip.top="t('Atualizar mensagens')"
              data-testid="support-detail-refresh-messages-button"
              @click="refreshMessages"
            />
          </div>

          <!-- Loading messages -->
          <div v-if="isLoadingMessages" class="loading-messages">
            <ProgressSpinner style="width: 40px; height: 40px" strokeWidth="4" />
            <span>{{ t('Carregando mensagens...') }}</span>
          </div>

          <!-- No messages -->
          <Message v-else-if="ticketMessages.length === 0" severity="info" :closable="false" class="no-messages-info">
            <template #icon>
              <i class="pi pi-inbox" style="font-size: 1.25rem"></i>
            </template>
            {{ t('Nenhuma mensagem ainda. Aguardando resposta do suporte.') }}
          </Message>

          <!-- Messages list -->
          <div v-else class="messages-container" ref="messagesContainer">
            <div
              v-for="message in ticketMessages"
              :key="message.id"
              class="message-wrapper"
              :class="{ 'message-wrapper-user': isUserMessage(message), 'message-wrapper-support': !isUserMessage(message) }"
            >
              <div class="message-bubble" :class="{ 'bubble-user': isUserMessage(message), 'bubble-support': !isUserMessage(message) }">
                <div class="message-meta">
                  <Avatar
                    :icon="isUserMessage(message) ? 'pi pi-user' : 'pi pi-headphones'"
                    :class="isUserMessage(message) ? 'avatar-user' : 'avatar-support'"
                    size="small"
                    shape="circle"
                  />
                  <span class="message-author-name">{{ getMessageAuthorDisplay(message) }}</span>
                  <span class="message-time">{{ formatMessageDateTime(message.date) }}</span>
                </div>
                <div class="message-content" v-html="sanitizeHtmlContent(message.body)"></div>
              </div>
            </div>
          </div>
        </div>

        <Divider />

        <!-- Reply Section -->
        <div class="reply-section">
          <div class="reply-header">
            <h4>
              <i class="pi pi-send"></i>
              {{ t('Enviar Resposta') }}
            </h4>
          </div>
          <InputRichText
            v-model="replyContent"
            :placeholder="t('Digite sua mensagem...')"
            :show-toolbar="true"
            min-height="100px"
            :disabled="isSendingReply"
            data-testid="support-detail-reply-input"
          />
          <div class="reply-actions">
            <Button
              :label="t('Enviar')"
              icon="pi pi-send"
              :loading="isSendingReply"
              :disabled="!isReplyValid"
              data-testid="support-detail-send-reply-button"
              @click="sendReply"
            />
          </div>
        </div>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, nextTick, type Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { sanitizeHtmlContent } from '../../utils/sanitizeHtmlContent';
import { eventBus } from '../../eventBus';
import { useOdooTicket } from '../../composables/useOdooTicket';
import { useDateFormatter } from '../../composables/useDateFormatter';
import { log } from '../../utils/DebugLogger';

interface Ticket {
  id: number | string;
  name?: string;
  stage_name?: string;
  created_at?: string;
  user_description?: string;
  description?: string;
  page_uri?: string;
  [key: string]: unknown;
}

interface TicketMessage {
  id: number | string;
  body?: string;
  date?: string;
  author_name?: string;
  is_user_message?: boolean;
  [key: string]: unknown;
}

interface OpenModalPayload {
  ticket?: Ticket;
}

interface ReplyResult {
  success: boolean;
  error?: string;
}

const { t } = useI18n();
const ticketStore = useOdooTicket();
const {
  fetchTicket,
  fetchTicketMessages,
  replyToTicket,
  clearCurrentTicket
} = ticketStore;
const currentTicket = ticketStore.currentTicket as unknown as Ref<Ticket | null>;
const ticketMessages = ticketStore.ticketMessages as unknown as Ref<TicketMessage[]>;
const { formatDateTime, fromApiDateTimeFormat } = useDateFormatter();

const isVisible = ref<boolean>(false);
const replyContent = ref<string>('');
const isSendingReply = ref<boolean>(false);
const isLoadingMessages = ref<boolean>(false);
const messagesContainer = ref<HTMLElement | null>(null);

const scrollToBottom = (): void => {
  void nextTick(() => {
    if (messagesContainer.value) {
      messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight;
    }
  });
};

const modalTitle = computed<string>(() => {
  const ticket = currentTicket.value;
  return ticket ? `${t('Chamado')} #${ticket.id}` : t('Detalhes do Chamado');
});

const isReplyValid = computed<boolean>(() => {
  if (!replyContent.value) return false;
  const stripped = replyContent.value.replace(/<[^>]*>/g, '').trim();
  return stripped.length > 0;
});

const formatTicketDateTime = (value?: string): string => {
  if (!value) return '';
  const dt = fromApiDateTimeFormat(value);
  return dt ? formatDateTime(dt) : '';
};

const loadTicketDetails = async (ticketId: number | string): Promise<void> => {
  log('TicketDetailModal', 'loadTicketDetails', 'Loading ticket details', ticketId);

  isLoadingMessages.value = true;

  try {
    const [ticketResult, messagesResult] = await Promise.all([
      fetchTicket(ticketId as number),
      fetchTicketMessages(ticketId as number)
    ]);

    log('TicketDetailModal', 'loadTicketDetails', 'Results', { ticketResult, messagesResult });
    scrollToBottom();
  } catch (err) {
    log('TicketDetailModal', 'loadTicketDetails', 'Error', err);
    eventBus.emit('globalMessage.error', t('Erro ao carregar detalhes do chamado'));
  } finally {
    isLoadingMessages.value = false;
  }
};

const openModal = async (data?: OpenModalPayload): Promise<void> => {
  log('TicketDetailModal', 'openModal', 'Opening modal', data);

  if (data?.ticket) {
    currentTicket.value = data.ticket;
  }

  isVisible.value = true;
  replyContent.value = '';

  if (data?.ticket?.id !== undefined) {
    await loadTicketDetails(data.ticket.id);
  }
};

const refreshMessages = async (): Promise<void> => {
  const ticket = currentTicket.value;
  if (!ticket?.id) return;

  isLoadingMessages.value = true;
  try {
    await fetchTicketMessages(ticket.id as number);
  } finally {
    isLoadingMessages.value = false;
  }
};

const closeModal = (): void => {
  log('TicketDetailModal', 'closeModal', 'Closing modal');
  isVisible.value = false;
  replyContent.value = '';
  clearCurrentTicket();
};

const sendReply = async (): Promise<void> => {
  const ticket = currentTicket.value;
  if (!isReplyValid.value || !ticket?.id) return;

  log('TicketDetailModal', 'sendReply', 'Sending reply');
  isSendingReply.value = true;

  try {
    const result = (await replyToTicket(ticket.id as number, replyContent.value)) as ReplyResult;

    if (result.success) {
      eventBus.emit('globalMessage.success', t('Resposta enviada com sucesso'));
      replyContent.value = '';

      await refreshMessages();
      scrollToBottom();

      eventBus.emit('ticket.replied', { ticketId: ticket.id });
    } else {
      eventBus.emit('globalMessage.error', result.error || t('Erro ao enviar resposta'));
    }
  } catch (err) {
    log('TicketDetailModal', 'sendReply', 'Error', err);
    eventBus.emit('globalMessage.error', t('Erro ao enviar resposta'));
  } finally {
    isSendingReply.value = false;
  }
};

const getStatusSeverity = (status?: string): string => {
  if (!status) return 'info';
  const statusLower = status.toLowerCase();

  if (
    statusLower.includes('resolvido') ||
    statusLower.includes('solved') ||
    statusLower.includes('fechado') ||
    statusLower.includes('closed')
  ) {
    return 'success';
  }
  if (
    statusLower.includes('em andamento') ||
    statusLower.includes('in progress') ||
    statusLower.includes('aguardando')
  ) {
    return 'warning';
  }
  if (statusLower.includes('cancelado') || statusLower.includes('cancelled')) {
    return 'danger';
  }
  return 'info';
};

const isUserMessage = (message: TicketMessage): boolean => {
  return message.is_user_message === true;
};

const getMessageAuthorDisplay = (message: TicketMessage): string => {
  if (isUserMessage(message)) {
    return message.author_name || t('Você');
  }
  return t('Suporte');
};

const formatMessageDateTime = (value?: string): string => formatTicketDateTime(value);

const handleModalOpen = (data?: OpenModalPayload): void => {
  log('TicketDetailModal', 'handleModalOpen', 'Modal open event received', data);
  void openModal(data);
};

onMounted(() => {
  log('TicketDetailModal', 'onMounted', 'Component mounted');
  eventBus.on('modal.ticket-detail.open', handleModalOpen);
  eventBus.on('modal.ticket-detail.close', closeModal);
});

onBeforeUnmount(() => {
  log('TicketDetailModal', 'onBeforeUnmount', 'Component unmounting');
  eventBus.off('modal.ticket-detail.open', handleModalOpen);
  eventBus.off('modal.ticket-detail.close', closeModal);
});
</script>

<style scoped>
.ticket-detail {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

/* Ticket Info Card */
.ticket-info-card {
  border: none;
  box-shadow: none;
  background: var(--surface-card);
}

.ticket-info-card :deep(.p-card-header) {
  padding: 0;
}

.ticket-info-card :deep(.p-card-body) {
  padding: 1rem 0 0 0;
}

.ticket-info-card :deep(.p-card-content) {
  padding: 0;
}

.ticket-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.ticket-title-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.ticket-id-badge {
  font-family: monospace;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-color-secondary);
  background: var(--surface-ground);
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
}

.ticket-subject-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--text-color);
  line-height: 1.4;
}

.ticket-subtitle {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--text-color-secondary);
  font-size: 0.875rem;
}

.description-panel {
  margin-top: 1rem;
}

.description-panel :deep(.p-panel-header) {
  background: var(--surface-ground);
  border-radius: 6px 6px 0 0;
  padding: 0.75rem 1rem;
}

.description-panel :deep(.p-panel-content) {
  padding: 1rem;
  background: var(--surface-ground);
  border-radius: 0 0 6px 6px;
}

.description-content {
  line-height: 1.6;
  color: var(--text-color);
}

.description-content :deep(p) {
  margin: 0 0 0.5rem 0;
}

.description-content :deep(p:last-child) {
  margin-bottom: 0;
}

/* Conversation Section */
.conversation-section {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.conversation-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.conversation-header h4 {
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-color);
}

.loading-messages {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  gap: 1rem;
  color: var(--text-color-secondary);
}

.no-messages-info {
  margin: 0;
}

/* Messages Container */
.messages-container {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  max-height: 350px;
  overflow-y: auto;
  padding: 1rem;
  background: #f8f9fa;
  border: 1px solid var(--surface-border);
  border-radius: 8px;
  scroll-behavior: smooth;
}

.message-wrapper {
  display: flex;
  max-width: 80%;
}

.message-wrapper-user {
  margin-left: auto;
  justify-content: flex-end;
}

.message-wrapper-support {
  margin-right: auto;
  justify-content: flex-start;
}

.message-bubble {
  padding: 0.75rem 1rem;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.bubble-user {
  background: transparent;
  color: var(--app-text-color);
  border: 2px solid var(--app-primary-color);
  border-bottom-right-radius: 4px;
}

.bubble-support {
  background: white;
  border: 1px solid #e0e0e0;
  color: var(--text-color);
  border-bottom-left-radius: 4px;
}

.message-meta {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  font-size: 0.75rem;
}

.avatar-user {
  background-color: var(--app-primary-color) !important;
  color: var(--app-secondary-color) !important;
}

.avatar-support {
  background-color: var(--app-success-color) !important;
  color: white !important;
}

.bubble-user .message-meta {
  flex-direction: row-reverse;
}

.message-author-name {
  font-weight: 600;
}

.bubble-user .message-author-name {
  color: var(--app-primary-color);
  font-weight: 600;
}

.bubble-support .message-author-name {
  color: var(--text-color);
}

.message-time {
  font-size: 0.7rem;
}

.bubble-user .message-time {
  color: var(--app-text-secondary-color);
}

.bubble-support .message-time {
  color: var(--text-color-secondary);
}

.message-content {
  line-height: 1.5;
  word-wrap: break-word;
}

.bubble-user .message-content {
  text-align: right;
  color: var(--app-text-color);
}

.bubble-support .message-content {
  color: var(--text-color);
}

.message-content :deep(p) {
  margin: 0 0 0.25rem 0;
}

.message-content :deep(p:last-child) {
  margin-bottom: 0;
}

.message-content :deep(a) {
  color: inherit;
  text-decoration: underline;
}

.message-content :deep(strong) {
  font-weight: 600;
}

/* Reply Section */
.reply-section {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.reply-header h4 {
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-color);
}

.reply-actions {
  display: flex;
  justify-content: flex-end;
}

/* Responsive */
@media (max-width: 768px) {
  .ticket-card-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .message-wrapper {
    max-width: 90%;
  }

  .messages-container {
    max-height: 280px;
  }
}
</style>
